import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useVideoCall } from '../../context/VideoCallContext';
import {
  Calendar,
  Stethoscope,
  LogOut,
  Search,
  MessageCircle,
  User,
  FileText,
  X,
  Wallet,
  CheckCircle2,
  Clock,
  BookOpen
} from 'lucide-react';
import appointmentService from '../../services/appointmentService';
import doctorService from '../../services/doctorService';
import medicalRecordService from '../../services/medicalRecordService';
import ChatBox from '../../components/common/ChatBox';
import MedicalRecordForm from '../../components/doctor/MedicalRecordForm';
import PermissionsAlert from '../../components/common/PermissionsAlert';
import WhatsappOptInToggle from '../../components/common/WhatsappOptInToggle';
import NequiEditor from '../../components/doctor/NequiEditor';
import DocumentEditor from '../../components/common/DocumentEditor';

function DashboardDoctor() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('appointments');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para chat
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showChat, setShowChat] = useState(false);

  // Estados para historia clínica (formulario + visualización)
  const [showMedicalRecord, setShowMedicalRecord] = useState(false);
  const [medicalRecord, setMedicalRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [selectedAppointmentForRecord, setSelectedAppointmentForRecord] = useState(null);

  // Estados para videollamada (se abre desde dentro del chat)
  // El modal de videollamada lo monta VideoCallProvider (uno solo para toda la
  // app), para que el timbre entrante funcione también desde el chat.
  const { openVideoCall } = useVideoCall();

  // Estados para balance
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'balance' && !balance) {
      loadBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadBalance = async () => {
    setLoadingBalance(true);
    try {
      const res = await doctorService.getMyBalance();
      setBalance(res.data);
    } catch (error) {
      console.error('Error loading balance:', error);
    } finally {
      setLoadingBalance(false);
    }
  };

  const fmtCOP = (n) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

  const loadData = async () => {
    try {
      const appointmentsRes = await appointmentService.getMyAppointments();
      setAppointments(appointmentsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMedicalRecord = async (appointment) => {
    setSelectedAppointmentForRecord(appointment);
    setShowMedicalRecord(true);
    setLoadingRecord(true);
    setMedicalRecord(null);

    try {
      const response = await medicalRecordService.getByAppointment(appointment._id);
      if (response && response.data) {
        console.log('✅ Historia clínica cargada:', response.data);
        setMedicalRecord(response.data);
      } else {
        console.log('ℹ️ No existe historia. Modo creación');
        setMedicalRecord(null);
      }
    } catch (error) {
      console.error('Error fetching medical record:', error);
      setMedicalRecord(null);
    } finally {
      setLoadingRecord(false);
    }
  };

  const handleCloseMedicalRecord = () => {
    setShowMedicalRecord(false);
    setMedicalRecord(null);
    setSelectedAppointmentForRecord(null);
    loadData();
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      'no-show': 'bg-red-100 text-red-800',
    };
    return badges[status] || badges.scheduled;
  };

  const getStatusText = (status) => {
    const texts = {
      scheduled: 'Agendada',
      confirmed: 'Confirmada',
      'in-progress': 'En Progreso',
      completed: 'Completada',
      cancelled: 'Cancelada',
      'no-show': 'No asistió',
    };
    return texts[status] || status;
  };

  const handleOpenChat = (appointment) => {
    setSelectedAppointment(appointment);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedAppointment(null);
  };

  const handleOpenVideoCall = (appointment) => {
    openVideoCall(appointment, appointment.patient);
  };

  // Motivo por el que NO se puede iniciar la consulta (null = se puede).
  // Las gratuitas quedan 'scheduled' pero con payment.status 'paid'.
  const getStartBlockReason = (appointment) => {
    if (appointment.status === 'cancelled') return 'Cita cancelada';
    if (appointment.status === 'no-show') return 'El paciente no asistió';
    if (appointment.status === 'scheduled' && appointment.payment?.status !== 'paid') {
      return 'Pago pendiente';
    }
    return null;
  };

  const getBogotaDay = (date) =>
    new Date(date).toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

  const filteredAppointments = appointments
    .filter(
      (appointment) =>
        appointment.patient?.user?.profile?.firstName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment.patient?.user?.profile?.lastName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Próximas primero (la más cercana arriba); después pasadas, canceladas,
      // completadas y no asistidas (la más reciente arriba). Una cita de hoy
      // cuenta como próxima aunque su hora ya haya pasado (puede estar en curso).
      const today = getBogotaDay(new Date());
      const isUpcoming = (x) =>
        !['cancelled', 'no-show', 'completed'].includes(x.status) &&
        getBogotaDay(x.scheduledDate) >= today;
      const upA = isUpcoming(a);
      const upB = isUpcoming(b);
      if (upA !== upB) return upA ? -1 : 1;
      const diff = new Date(a.scheduledDate) - new Date(b.scheduledDate);
      return upA ? diff : -diff;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Stethoscope className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DocEnLinea</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {user?.profile?.firstName} {user?.profile?.lastName}
                </div>
                <div className="text-xs text-gray-500">Doctor</div>
              </div>
              {/* Guía de uso: página estática en public/guia-medico.html. Se abre en
                  otra pestaña para no interrumpir una consulta en curso. */}
              <a
                href="/guia-medico.html"
                target="_blank"
                rel="noopener noreferrer"
                title="Guía del médico: uso básico de la plataforma"
                className="btn-secondary flex items-center"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Guía del médico</span>
                <span className="sm:hidden">Guía</span>
              </a>
              <button onClick={logout} className="btn-secondary flex items-center">
                <LogOut className="h-4 w-4 mr-2" />
                Salir
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenido, Dr. {user?.profile?.firstName}
          </h1>
          <p className="text-gray-600">
            Panel de control médico - Gestiona tus consultas y pacientes
          </p>
        </div>

        <PermissionsAlert />

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('appointments')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'appointments'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Calendar className="inline h-5 w-5 mr-2" />
                Mis Citas ({appointments.length})
              </button>
              <button
                onClick={() => setActiveTab('patients')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'patients'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="inline h-5 w-5 mr-2" />
                Mis Pacientes
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'profile'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MessageCircle className="inline h-5 w-5 mr-2" />
                Perfil
              </button>
              <button
                onClick={() => setActiveTab('balance')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'balance'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Wallet className="inline h-5 w-5 mr-2" />
                Balance
              </button>
            </nav>
          </div>

          {activeTab === 'profile' && (
            <div className="p-6 space-y-6">
              <DocumentEditor />
              <NequiEditor />
              <WhatsappOptInToggle />
            </div>
          )}

          {activeTab === 'appointments' && (
            <div className="p-6">
              {appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg text-gray-500">No tienes citas agendadas</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar paciente por nombre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {filteredAppointments.map((appointment) => (
                    <div
                      key={appointment._id}
                      className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600 font-semibold">Paciente</p>
                          <p className="text-lg font-bold text-gray-800">
                            {appointment.patient?.user?.profile?.firstName}{' '}
                            {appointment.patient?.user?.profile?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{appointment.patient?.user?.email}</p>
                          {appointment.patient?.user?.documentNumber && (
                            <p className="text-xs text-gray-500">
                              {appointment.patient?.user?.documentType || 'CC'}: {appointment.patient?.user?.documentNumber}
                            </p>
                          )}
                          {appointment.patient?.user?.profile?.phone && (
                            <p className="text-xs text-gray-500">
                              Tel: {appointment.patient?.user?.profile?.phone}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 font-semibold">Fecha y Hora</p>
                          <p className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {new Date(appointment.scheduledDate).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}
                            {getBogotaDay(appointment.scheduledDate) === getBogotaDay(new Date()) && (
                              <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold">Hoy</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 font-semibold">Estado</p>
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                              getStatusBadge(appointment.status)
                            }`}
                          >
                            {getStatusText(appointment.status)}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-gray-600 font-semibold">Motivo de Consulta</p>
                        <p className="text-gray-800">{appointment.reason}</p>
                        {appointment.symptoms && (
                          <>
                            <p className="text-sm text-gray-600 font-semibold mt-2">Síntomas</p>
                            <p className="text-gray-800">{appointment.symptoms}</p>
                          </>
                        )}
                      </div>

                      {/* Un unico boton visible para iniciar la consulta: Chat. Desde dentro del chat se puede pasar a video. */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                          onClick={() => handleOpenChat(appointment)}
                          disabled={!!getStartBlockReason(appointment)}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MessageCircle className="h-4 w-4" />
                          <span>Iniciar consulta (Chat)</span>
                        </button>
                        <button
                          onClick={() => handleOpenMedicalRecord(appointment)}
                          disabled={loadingRecord}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Historias</span>
                        </button>
                        <button
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold text-sm cursor-default"
                        >
                          <span className="text-xs">ID: {appointment._id?.slice(-6)}</span>
                        </button>
                      </div>
                      {getStartBlockReason(appointment) && (
                        <p className="mt-2 text-sm font-semibold text-amber-700">
                          {getStartBlockReason(appointment)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'patients' && (
            <div className="p-6">
              <div className="text-center py-12">
                <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg text-gray-500">Vista de pacientes en desarrollo</p>
              </div>
            </div>
          )}

          {activeTab === 'balance' && (
            <div className="p-6">
              {loadingBalance ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Cargando balance...</p>
                </div>
              ) : !balance ? (
                <div className="text-center py-12">
                  <Wallet className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg text-gray-500">No se pudo cargar el balance</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Clock className="h-5 w-5 text-yellow-600" />
                        </div>
                        <p className="text-sm text-gray-600 font-semibold">Por cobrar</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{fmtCOP(balance.totalNetToReceive)}</p>
                      <p className="text-xs text-gray-500 mt-1">{balance.pendingCount} consulta(s) pendiente(s)</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                        <p className="text-sm text-gray-600 font-semibold">Pagado</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{fmtCOP(balance.totalPaidOut)}</p>
                      <p className="text-xs text-gray-500 mt-1">Total transferido a tu Nequi</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <Wallet className="h-5 w-5 text-primary-600" />
                        </div>
                        <p className="text-sm text-gray-600 font-semibold">Consultas completadas</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{balance.totalCompletedAppointments}</p>
                      <p className="text-xs text-gray-500 mt-1">Completadas y pagadas por el paciente</p>
                    </div>
                  </div>

                  {balance.appointments.length === 0 ? (
                    <div className="text-center py-12">
                      <Wallet className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg text-gray-500">Aún no tienes consultas para cobrar</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              {['Fecha', 'Paciente', 'Monto neto', 'Estado pago', 'Fecha pago'].map((h) => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {balance.appointments.map((a) => (
                              <tr key={a.appointmentId} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-700">
                                  {new Date(a.scheduledDate).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}
                                </td>
                                <td className="px-4 py-3 text-gray-900 font-medium">{a.patientName}</td>
                                <td className="px-4 py-3 text-gray-900 tabular-nums">{fmtCOP(a.amount)}</td>
                                <td className="px-4 py-3">
                                  {a.payoutStatus === 'paid' ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                      <CheckCircle2 className="w-3 h-3" />Pagado
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                                      <Clock className="w-3 h-3" />Pendiente
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-gray-500">
                                  {a.payoutDate ? new Date(a.payoutDate).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' }) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showChat && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 p-2 border-b" data-testid="chat-header">
              {/* Deja claro qué cita se está atendiendo */}
              <div className="min-w-0 pl-2">
                <p className="font-bold text-gray-800 truncate">
                  {selectedAppointment.patient?.user?.profile?.firstName}{' '}
                  {selectedAppointment.patient?.user?.profile?.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(selectedAppointment.scheduledDate).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}
                  {' · '}#{selectedAppointment._id?.slice(-6)}
                </p>
              </div>
              <button
                onClick={handleCloseChat}
                className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 p-2 rounded-full transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ChatBox
                appointment={selectedAppointment}
                otherUser={selectedAppointment.patient}
                onClose={handleCloseChat}
                onOpenVideoCall={() => handleOpenVideoCall(selectedAppointment)}
              />
            </div>
          </div>
        </div>
      )}

      {showMedicalRecord && selectedAppointmentForRecord && (
        loadingRecord ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Cargando historia clínica...</p>
            </div>
          </div>
        ) : (
          <MedicalRecordForm
            appointment={selectedAppointmentForRecord}
            initialData={medicalRecord}
            // El borrador local de la historia se guarda por médico y cita.
            userId={user?._id || user?.id}
            onClose={handleCloseMedicalRecord}
            onSuccess={handleCloseMedicalRecord}
          />
        )
      )}
    </div>
  );
}

export default DashboardDoctor;
